import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Input from "../../components/ui/Input";
import { REPLY_LENGTH_OPTIONS } from "../../config/replyLengthOptions";
import { TONE_OPTIONS, normalizeToneValue } from "../../config/toneOptions";
import { userProfileQueryKey, userProfileService } from "../../services/userProfile";

const SettingsReplies = () => {
  const queryClient = useQueryClient();
  const [saveMessage, setSaveMessage] = useState("");

  const { data: profile } = useQuery({
    queryKey: userProfileQueryKey,
    queryFn: userProfileService.get,
  });

  const saveMutation = useMutation({
    mutationFn: userProfileService.update,
    onSuccess: () => {
      setSaveMessage("Saved.");
      setTimeout(() => setSaveMessage(""), 2000);
      queryClient.invalidateQueries({ queryKey: userProfileQueryKey });
    },
  });

  const [tone, setTone] = useState("helpful_peer");
  const [replyLength, setReplyLength] = useState("medium");
  const [replyCustomContext, setReplyCustomContext] = useState("");

  useEffect(() => {
    if (!profile) return;
    if (profile.tone) setTone(normalizeToneValue(profile.tone));
    if (profile.reply_length) setReplyLength(profile.reply_length);
    else setReplyLength("medium");
    setReplyCustomContext(profile.reply_custom_context || "");
  }, [profile]);

  const patchProfile = (payload) => {
    if (!profile) return;
    saveMutation.mutate(payload);
  };

  const selectTone = (value) => {
    if (!profile || value === tone) return;
    setTone(value);
    patchProfile({ tone: value });
  };

  const selectReplyLength = (value) => {
    if (!profile || value === replyLength) return;
    setReplyLength(value);
    patchProfile({ reply_length: value });
  };

  const saveCustomHints = (e) => {
    e.preventDefault();
    if (!profile) return;
    saveMutation.mutate({ reply_custom_context: replyCustomContext });
  };

  return (
    <div className="space-y-6">
      <div className="hidden md:block">
        <h1 className="font-display font-bold italic text-[40px] leading-[1.1] text-theme-on-background">Replies</h1>
        <p className="font-mono text-[12px] text-theme-dim mt-1 uppercase tracking-[0.08em]">
          Defaults for drafted replies before you tweak each thread.
        </p>
      </div>

      <Card>
        <h2 className="font-display font-bold text-lg text-theme-on-background">Default tone</h2>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {TONE_OPTIONS.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => selectTone(item.value)}
              disabled={!profile}
              className={`rounded border p-3 text-left font-mono text-xs tracking-[0.04em] transition-colors ${
                tone === item.value
                  ? "border-theme-accent bg-theme-accent-muted text-theme-accent"
                  : "border-theme-border bg-theme-main text-theme-muted hover:border-theme-border-bright"
              }`}
            >
              <span className="block font-display font-semibold">{item.title}</span>
              <span className="mt-2 block leading-relaxed opacity-85">{item.subtitle}</span>
              <span className="mt-3 block italic text-[11px] opacity-75">{item.example}</span>
            </button>
          ))}
        </div>
      </Card>

      <Card>
        <h2 className="font-display font-bold text-lg text-theme-on-background">Reply length</h2>
        <p className="font-mono text-[11px] text-theme-muted mt-1 mb-4">
          How verbose AI drafts should be by default (you can override per lead when regenerating).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {REPLY_LENGTH_OPTIONS.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => selectReplyLength(item.value)}
              disabled={!profile}
              className={`rounded border p-3 text-left font-mono text-xs tracking-[0.04em] transition-colors ${
                replyLength === item.value
                  ? "border-theme-accent bg-theme-accent-muted text-theme-accent"
                  : "border-theme-border bg-theme-main text-theme-muted hover:border-theme-border-bright"
              }`}
            >
              <span className="block font-display font-semibold">{item.title}</span>
              <span className="mt-2 block leading-relaxed opacity-85">{item.subtitle}</span>
            </button>
          ))}
        </div>
      </Card>

      <Card>
        <h2 className="font-display font-bold text-lg text-theme-on-background">Optional reply hints</h2>
        <p className="font-mono text-[11px] text-theme-muted mt-1 mb-4">
          Global instructions for AI drafts. Use Save when you edit this box; tone and length save on their own.
        </p>
        <form className="space-y-4" onSubmit={saveCustomHints}>
          <Input
            label="Custom instructions"
            multiline
            rows={4}
            placeholder="Follow-up style, calls to action, things to avoid."
            value={replyCustomContext}
            onChange={(e) => setReplyCustomContext(e.target.value)}
          />
          <div className="flex flex-wrap items-center justify-end gap-3">
            {saveMessage ? <p className="font-mono text-xs text-theme-accent mr-auto">{saveMessage}</p> : null}
            <Button type="submit" loading={saveMutation.isPending} disabled={!profile}>
              Save hints
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default SettingsReplies;
